import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuth } from 'context/auth'
import PageHeader from 'components/page-header'
import IconRail from './icon-rail'
import TopBar from './top-bar'
import MobileMenu from './mobile-menu'
import CommandPalette from './command-palette'
import AccountBanner from './account-banner'
import ContentErrorBoundary from './content-error-boundary'
import { getActiveEntry, getNavEntries } from './nav-config'

const RAIL_STORAGE_KEY = 'li.shell.railExpanded'

const readRailExpanded = () => {
  try {
    return window.localStorage.getItem(RAIL_STORAGE_KEY) === 'true'
  } catch (error) {
    return false
  }
}

const writeRailExpanded = (value) => {
  try {
    window.localStorage.setItem(RAIL_STORAGE_KEY, String(value))
  } catch (error) {}
}

function DashboardShell({ children }) {
  const { currentUser } = useAuth()
  const { pathname } = useLocation()
  const [expanded, setExpanded] = useState(readRailExpanded)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [paletteOpen, setPaletteOpen] = useState(false)

  const entries = useMemo(() => getNavEntries(currentUser), [currentUser])
  const activeEntry = getActiveEntry(pathname, currentUser)

  const toggleRail = useCallback(() => setExpanded((value) => !value), [])

  useEffect(() => {
    writeRailExpanded(expanded)
  }, [expanded])

  useEffect(() => {
    setMobileOpen(false)
  }, [pathname])

  useEffect(() => {
    const onKeyDown = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        setMobileOpen(false)
        setPaletteOpen(true)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  return (
    <div className='tw-min-h-screen tw-min-w-0 tw-bg-canvas'>
      <IconRail entries={entries} activeKey={activeEntry?.key} expanded={expanded} variant='fixed' />
      <MobileMenu
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        entries={entries}
        activeKey={activeEntry?.key}
      />

      <div
        className={`tw-flex tw-min-h-screen tw-min-w-0 tw-flex-col tw-transition-[padding] tw-duration-200 ${
          expanded ? 'md:tw-pl-[220px]' : 'md:tw-pl-[72px]'
        }`}
      >
        <AccountBanner />
        <TopBar
          expanded={expanded}
          onToggleRail={toggleRail}
          mobileOpen={mobileOpen}
          onOpenMobile={() => setMobileOpen(true)}
          onOpenPalette={() => setPaletteOpen(true)}
        />
        {activeEntry?.tabs?.length > 0 && (
          <div className='tw-min-w-0 tw-px-5 tw-pt-5'>
            <PageHeader title={activeEntry.label} tabs={activeEntry.tabs} />
          </div>
        )}
        <main className='tw-min-w-0 tw-flex-1 tw-overflow-x-clip tw-p-5'>
          <ContentErrorBoundary resetKey={pathname}>{children}</ContentErrorBoundary>
        </main>
      </div>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} entries={entries} />
    </div>
  )
}

export default DashboardShell
